import React from 'react';
import { Loader2 } from 'lucide-react';

/** Small shared pieces for the Agent Team pages. */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
export const BASE = `${API_URL}/api/agent-team`;

export const api = async (path, { method = 'GET', body } = {}) => {
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  let data;
  try {
    data = await response.json();
  } catch {
    data = { success: false, message: `The server answered ${response.status}.` };
  }
  if (!data.success) {
    const error = new Error(data.message || 'Something went wrong.');
    // e.g. 'UPGRADE_REQUIRED' — a refusal the page can act on, not just print.
    if (data.code) error.code = data.code;
    throw error;
  }
  return data;
};

export const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

export const STATUS = {
  scouted: { label: 'Scouted', cls: 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300' },
  review: { label: 'Needs review', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  ready: { label: 'Approved', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  queued: { label: 'Email queued', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  contacted: { label: 'Contacted', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  replied: { label: 'Replied', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  negotiating: { label: 'Negotiating', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  closing: { label: 'Closing', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  won: { label: 'Won', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  lost: { label: 'Lost', cls: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400' },
  needs_human: { label: 'Needs a person', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  no_reply: { label: 'No reply', cls: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400' },
  on_hold: { label: 'On hold', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  rejected: { label: 'Rejected', cls: 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-500' },
  no_contact: { label: 'No contact', cls: 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-500' },
  bounced: { label: 'All bounced', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  opted_out: { label: 'Opted out', cls: 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-500' },
  paused: { label: 'Paused (person)', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' }
};

export const AGENT_LABEL = {
  scout: 'Scout',
  vetter: 'Vetter',
  outreach: 'Contact agent',
  strategist: 'Negotiator',
  negotiator: 'Affiliate Manager',
  classifier: 'Reply classifier',
  lessons: 'Lesson learner',
  stopper: 'Stop agent',
  human: 'You',
  system: 'System'
};

/**
 * Which tier a run used, never which model.
 *
 * Model ids are an internal matter: the page says "fast model" or "strong
 * model" and nothing more. AgentTeam fills this in from /meta, where every
 * role carries both its model and its tier, so an admin moving an agent onto
 * another model in the User Panel still gets the right label on its runs.
 */
let MODEL_TIER = {};

export const rememberModelTiers = (roles = []) => {
  MODEL_TIER = {};
  (roles || []).forEach((r) => {
    if (!r || !r.model) return;
    // An admin can put a fast agent on the strong model. Then that model id
    // belongs to both tiers, and the strong one is the truthful label.
    if (r.tier !== 'fast') MODEL_TIER[r.model] = 'strong';
    else if (!MODEL_TIER[r.model]) MODEL_TIER[r.model] = 'fast';
  });
};

// Null for a model no agent is on any more: better to show nothing than an id.
export const tierLabel = (model) => {
  const tier = model ? MODEL_TIER[model] : null;
  return tier ? (tier === 'fast' ? 'Fast model' : 'Strong model') : null;
};

export const KIND_LABEL = {
  first_touch: 'First email',
  followup: 'Follow-up',
  reply: 'Reply',
  closing: 'Deal confirmation',
  bounce: 'Bounce',
  auto_reply: 'Auto-reply'
};

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, cls: 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300' };
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

export const Button = ({ children, onClick, variant = 'secondary', disabled, busy, icon: Icon, type = 'button', className = '', title }) => {
  const styles = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-900',
    secondary: 'border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
    danger: 'border border-red-200 bg-white text-red-700 hover:bg-red-50 dark:border-red-900 dark:bg-slate-900 dark:text-red-300 dark:hover:bg-red-950',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-300'
  };
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled || busy}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${styles[variant]} ${className}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
};

// `tour` marks the box for the setup walk-through to spotlight. See SetupTour.
export const Section = ({ title, hint, children, actions, tour }) => (
  <section data-tour={tour} className="rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{hint}</p>}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

export const Field = ({ label, hint, children, className = '', tour }) => (
  <label data-tour={tour} className={`block ${className}`}>
    <span className="mb-1 block text-xs font-medium text-gray-700 dark:text-slate-300">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-gray-500 dark:text-slate-400">{hint}</span>}
  </label>
);

export const Chips = ({ options, value = [], onChange, render = (o) => o.label || o }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const key = o.key || o.code || o;
      const on = value.includes(key);
      return (
        <button
          key={key}
          type="button"
          onClick={() => onChange(on ? value.filter((v) => v !== key) : [...value, key])}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
            on
              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
          }`}
        >
          {render(o)}
        </button>
      );
    })}
  </div>
);

export const formatWhen = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export const timeAgo = (value) => {
  if (!value) return '—';
  const diff = (Date.now() - new Date(value).getTime()) / 1000;
  const future = diff < 0;
  const s = Math.abs(diff);
  const unit = s < 60 ? `${Math.round(s)}s` : s < 3600 ? `${Math.round(s / 60)}m` : s < 86400 ? `${Math.round(s / 3600)}h` : `${Math.round(s / 86400)}d`;
  return future ? `in ${unit}` : `${unit} ago`;
};

export const Notice = ({ tone = 'info', children }) => {
  const tones = {
    info: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200',
    warn: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
    error: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300',
    ok: 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-200'
  };
  return <div className={`rounded-lg border px-3 py-2 text-sm ${tones[tone]}`}>{children}</div>;
};

export const describeTerms = (t, currency) => {
  if (!t) return null;
  const cur = t.currency || currency || '';
  const parts = [];
  if (t.revshare_pct != null) parts.push(`${t.revshare_pct}% RevShare`);
  if (t.cpa != null) parts.push(`${t.cpa} ${cur} CPA`);
  if (t.baseline != null) parts.push(`baseline ${t.baseline} ${cur}`);
  if (t.fixed_fee_monthly != null) parts.push(`${t.fixed_fee_monthly} ${cur}/month`);
  if (t.notes) parts.push(t.notes);
  return parts.join(', ') || t.structure;
};
